import { useLoader, useThree } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import React, { useEffect, forwardRef, useRef } from "react";
import * as THREE from "three";
import { Edges } from "@react-three/drei";

interface MaterialProps {
  roughness?: number;
  metalness?: number;
  envMapIntensity?: number;
}

interface ResultProps {
  url: string;
  showWireframe: boolean;
  materialProps?: MaterialProps;
  onLoad?: () => void;
}

const Result = forwardRef<THREE.Group, ResultProps>(
  ({ url, showWireframe, materialProps, onLoad }, ref) => {
    const gltf = useLoader(GLTFLoader, url);
    const { camera } = useThree();
    const cameraPositionedRef = useRef(false);

    // First useEffect: Camera setup (runs only once when model loads)
    useEffect(() => {
      // Only position camera once per model URL
      if (cameraPositionedRef.current) {
        // If camera was already positioned, just call onLoad
        onLoad?.();
        return;
      }

      const box = new THREE.Box3().setFromObject(gltf.scene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
    
      gltf.scene.position.y = -box.min.y;
    
      if (camera instanceof THREE.PerspectiveCamera) {
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = (camera.fov * Math.PI) / 180;
        const distance = maxDim / (2 * Math.tan(fov / 2));
    
        camera.position.set(
          center.x + distance,
          center.y + distance,
          center.z + distance,
        );
      }
      camera.lookAt(center);
      camera.updateProjectionMatrix();
      
      // Mark camera as positioned
      cameraPositionedRef.current = true;
    
      // Call onLoad callback if provided
      onLoad?.();
    }, [gltf, camera, onLoad]);

    // Reset camera positioned flag when URL changes
    useEffect(() => {
      cameraPositionedRef.current = false;
    }, [url]);

    const renderScene = (object: THREE.Object3D) => {
      return (
        <group ref={ref}>
          {object.children.map((child) => {
            if (child instanceof THREE.Mesh) {
              return (
                <group key={child.uuid}>
                  <mesh
                    geometry={child.geometry}
                    material={
                      new THREE.MeshStandardMaterial({
                        map: child.material.map,
                        color: child.material.color,
                        roughness: materialProps?.roughness ?? 0.5,
                        metalness: materialProps?.metalness ?? 0.1,
                        envMapIntensity: materialProps?.envMapIntensity ?? 1.2,
                        transparent: showWireframe,
                        opacity: showWireframe ? 0.3 : 1.0,
                      })
                    }
                    position={child.position}
                    rotation={child.rotation}
                    scale={child.scale}
                  >
                    {showWireframe && (
                      <Edges scale={1} threshold={1} color="#9d4b4b" />
                    )}
                  </mesh>
                </group>
              );
            } else {
              return renderScene(child);
            }
          })}
        </group>
      );
    };

    return <>{renderScene(gltf.scene)}</>;
  }
);

export default Result;
